import React, { useState } from "react";
import { BRAND } from "../config/brand";
import { HomepageDesktop, HomepageMobile } from "../blueprint/HomepageMock";
import { CandidateDesktop, CandidateMobile } from "../blueprint/CandidateMock";
import { EmployerDesktop, EmployerMobile } from "../blueprint/EmployerMock";
import { AdminDesktop, AdminMobile } from "../blueprint/AdminMock";
import { SchemaSection, PermissionsSection, PhasesSection } from "../blueprint/DataSections";
import { Monitor, Smartphone, Palette, Database, Shield, GitBranch, CheckCircle2 } from "lucide-react";

const NAV = [
  ["designs", "Screen Designs", Palette],
  ["schema", "Database", Database],
  ["permissions", "Permissions", Shield],
  ["plan", "Build Plan", GitBranch],
];

const ScreenBlock = ({ id, title, desc, Desktop, Mobile }) => {
  const [view, setView] = useState("desktop");
  return (
    <div id={id} className="scroll-mt-24">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-serif text-2xl text-[#173A2C]">{title}</h3>
          <p className="mt-1 max-w-xl text-[13px] text-[#5C5855]">{desc}</p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-[#E8E4DB] bg-white p-1">
          <button data-testid={`${id}-view-desktop`} onClick={() => setView("desktop")} className={`flex items-center gap-1 rounded-full px-3 py-1 text-[11px] ${view === "desktop" ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}><Monitor className="h-3.5 w-3.5" /> Desktop</button>
          <button data-testid={`${id}-view-mobile`} onClick={() => setView("mobile")} className={`flex items-center gap-1 rounded-full px-3 py-1 text-[11px] ${view === "mobile" ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}><Smartphone className="h-3.5 w-3.5" /> Mobile</button>
        </div>
      </div>
      <div className="hidden gap-8 lg:flex">
        <div className="flex-1"><Desktop /></div>
        <div className="w-[248px] shrink-0"><Mobile /></div>
      </div>
      <div className="lg:hidden">{view === "desktop" ? <Desktop /> : <Mobile />}</div>
    </div>
  );
};

export default function Blueprint() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 border-b border-[#E8E4DB] bg-[#FDFBF7]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-[#1F4A38] text-[12px] font-bold text-[#C4A46B]">{BRAND.shortName}</div>
            <div>
              <div className="font-serif text-lg leading-none text-[#173A2C]">{BRAND.name}</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#C4A46B]">Product Blueprint · Phase 0</div>
            </div>
          </div>
          <nav className="hidden gap-1 md:flex">
            {NAV.map(([id, label, Icon]) => (
              <a key={id} href={`#${id}`} className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] text-[#2C2A29] hover:bg-white">
                <Icon className="h-3.5 w-3.5 text-[#C4A46B]" /> {label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* Intro */}
      <section className="mx-auto max-w-6xl px-5 pt-10">
        <div className="max-w-3xl">
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C4A46B]">Review & approve</span>
          <h1 className="mt-2 font-serif text-4xl font-light leading-tight text-[#173A2C] sm:text-5xl">
            Equestrian community & international careers platform
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#5C5855]">
            Below is the full blueprint before we build: the four key screens (desktop + mobile), the database
            structure, the role-permission matrix, and a phased build sequence with acceptance tests. Working
            name <b className="text-[#2C2A29]">"{BRAND.name}"</b> — {BRAND.tagline}. {BRAND.name} branding lives in one config file for easy change.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-[12px]">
            {["JWT auth + email OTP", "Private object storage", "Backend-enforced permissions", "Installable PWA", "Audit trail"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full border border-[#E8E4DB] bg-white px-3 py-1 text-[#2C2A29]"><CheckCircle2 className="h-3.5 w-3.5 text-[#2E6B4B]" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Designs */}
      <section id="designs" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12">
        <div className="mb-8"><span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C4A46B]">01 — Screen designs</span><h2 className="mt-1 font-serif text-3xl text-[#173A2C]">The four key screens</h2></div>
        <div className="space-y-14">
          <ScreenBlock id="home" title="Public Homepage & Feed" desc="Premium equestrian hero — 'Where Equestrian Talent Meets Global Opportunity' — with the join CTA, professions, feed categories, featured jobs and clear candidate/employer entry points." Desktop={HomepageDesktop} Mobile={HomepageMobile} />
          <ScreenBlock id="candidate" title="Candidate Dashboard" desc="Supports multiple equestrian professions (Groomer, Rider, Jockey, Farrier, Trainer…) with role-specific skills, a profile-completeness checklist, approval status, media/documents, and a per-application journey." Desktop={CandidateDesktop} Mobile={CandidateMobile} />
          <ScreenBlock id="employer" title="Employer Dashboard" desc="Verified-only talent search with a profession filter and masked aliases (HG-###), role-specific skills, privacy guardrails, rate-limited & logged views. No bulk export, no original CV." Desktop={EmployerDesktop} Mobile={EmployerMobile} />
          <ScreenBlock id="admin" title="Admin Dashboard" desc="Approval queues for candidates & employers, pending-revision review, feed/job moderation, stage management and a full audit log." Desktop={AdminDesktop} Mobile={AdminMobile} />
        </div>
      </section>

      {/* Schema */}
      <section id="schema" className="scroll-mt-24 border-t border-[#E8E4DB] bg-white/40 py-12">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-8"><span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C4A46B]">02 — Data model</span><h2 className="mt-1 font-serif text-3xl text-[#173A2C]">Database structure (MongoDB)</h2><p className="mt-1 max-w-2xl text-[13px] text-[#5C5855]">Collections and key fields. Private identity data is separated from employer-visible fields; approved snapshots freeze what an employer/application saw.</p></div>
          <SchemaSection />
        </div>
      </section>

      {/* Permissions */}
      <section id="permissions" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12">
        <div className="mb-8"><span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C4A46B]">03 — Access control</span><h2 className="mt-1 font-serif text-3xl text-[#173A2C]">Role-permission matrix</h2></div>
        <PermissionsSection />
      </section>

      {/* Plan */}
      <section id="plan" className="scroll-mt-24 border-t border-[#E8E4DB] bg-white/40 py-12">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-8"><span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C4A46B]">04 — Delivery</span><h2 className="mt-1 font-serif text-3xl text-[#173A2C]">Build sequence & acceptance tests</h2></div>
          <PhasesSection />
        </div>
      </section>

      {/* Approve footer */}
      <section className="mx-auto max-w-6xl px-5 pb-16">
        <div className="rounded-md border border-[#1F4A38] bg-[#1F4A38] p-8 text-center text-white">
          <h3 className="font-serif text-2xl">Approve to start Phase 1</h3>
          <p className="mx-auto mt-2 max-w-xl text-[13px] text-white/80">Reply with approval (or any changes to designs, schema, permissions or sequence). Once approved, I'll build Phase 1 — Foundation & Auth — and report what works, what was tested, and what remains.</p>
        </div>
        <p className="mt-4 text-center text-[11px] text-[#5C5855]">Photos are temporary, properly-licensed stock for review. Final licensed/owned media will be requested before publication.</p>
      </section>
    </div>
  );
}
